import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Maximize2, 
  Minimize2,
  Play,
  Pause,
  Volume2,
  VolumeX,
  FileText,
  Image,
  Video,
  Music
} from 'lucide-react';

const MediaPreviewModal = ({ 
  isOpen, 
  onClose, 
  media, 
  onDownload 
}) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const mediaRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setRotation(0);
      setIsPlaying(false);
      setIsMuted(false);
      setCurrentTime(0);
    }
  }, [isOpen, media]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleRotate();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          handleToggleFullscreen();
          break;
        case ' ':
          e.preventDefault();
          if (media?.type?.startsWith('video/') || media?.type?.startsWith('audio/')) {
            handleTogglePlay();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, media, scale]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleTogglePlay = () => {
    if (mediaRef.current) {
      if (isPlaying) {
        mediaRef.current.pause();
      } else {
        mediaRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleToggleMute = () => {
    if (mediaRef.current) {
      mediaRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (mediaRef.current) {
      setDuration(mediaRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (mediaRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      mediaRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getMediaIcon = () => {
    if (media?.type?.startsWith('image/')) return <Image className="w-6 h-6" />;
    if (media?.type?.startsWith('video/')) return <Video className="w-6 h-6" />;
    if (media?.type?.startsWith('audio/')) return <Music className="w-6 h-6" />;
    return <FileText className="w-6 h-6" />;
  };

  const getMediaType = () => {
    if (media?.type?.startsWith('image/')) return 'Image';
    if (media?.type?.startsWith('video/')) return 'Video';
    if (media?.type?.startsWith('audio/')) return 'Audio';
    return 'Document';
  };

  if (!isOpen || !media) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div 
        ref={containerRef}
        className="relative bg-white rounded-lg max-w-2xl w-full max-h-[60vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            {/* Back/Close Button */}
            <button
              onClick={onClose}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors mr-2"
              title="Back/Close"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            
            {getMediaIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {media.name || 'Media Preview'}
              </h3>
              <p className="text-sm text-gray-500">{getMediaType()}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            {(media.type?.startsWith('image/') || media.type?.startsWith('video/')) && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Zoom Out (-)"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Zoom In (+)"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Rotate (R)"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
              </>
            )}
            
            {/* Fullscreen Toggle */}
            <button
              onClick={handleToggleFullscreen}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Toggle Fullscreen (F)"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            
            {/* Download Button */}
            <button
              onClick={() => onDownload(media)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Media Content */}
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4 relative">
          {/* Mobile Close Button - Bottom Center */}
          <button
            onClick={onClose}
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 p-4 bg-black bg-opacity-70 hover:bg-opacity-90 text-white rounded-full transition-all duration-200 shadow-lg md:hidden"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div 
            className="relative"
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            {/* Image */}
            {media.type?.startsWith('image/') && (
              <div className="relative">
                <img
                  src={media.url}
                  alt={media.name}
                  className="max-w-full max-h-[40vh] object-contain rounded-lg shadow-lg"
                  style={{ cursor: scale > 1 ? 'grab' : 'default' }}
                />
                
                {/* Close Button Overlay */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-3 bg-black bg-opacity-70 hover:bg-opacity-90 text-white rounded-full transition-all duration-200 shadow-lg"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            )}
            
            {/* Video */}
            {media.type?.startsWith('video/') && (
              <div className="relative">
                <video
                  ref={mediaRef}
                  src={media.url}
                  className="max-w-full max-h-[40vh] rounded-lg shadow-lg"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />
                
                {/* Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleTogglePlay}
                      className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
                    </button>
                    
                    <button
                      onClick={handleToggleMute}
                      className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                    </button>
                    
                    <div className="flex-1">
                      <div
                        className="w-full h-2 bg-white/30 rounded-full cursor-pointer"
                        onClick={handleSeek}
                      >
                        <div
                          className="h-full bg-white rounded-full transition-all"
                          style={{ width: `${(currentTime / duration) * 100}%` }}
                        />
                      </div>
                    </div>
                    
                    <span className="text-white text-sm min-w-[80px]">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Audio */}
            {media.type?.startsWith('audio/') && (
              <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
                <div className="flex items-center space-x-4 mb-4">
                  <button
                    onClick={handleTogglePlay}
                    className="p-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  
                  <button
                    onClick={handleToggleMute}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  
                  <div className="flex-1">
                    <div
                      className="w-full h-2 bg-gray-200 rounded-full cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div
                        className="h-full bg-purple-600 rounded-full transition-all"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  <span className="text-sm text-gray-600 min-w-[80px]">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                
                <div className="text-center">
                  <Music className="w-16 h-16 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900">{media.name}</h4>
                  <p className="text-sm text-gray-500">Audio File</p>
                </div>
              </div>
            )}
            
            {/* Document */}
            {!media.type?.startsWith('image/') && !media.type?.startsWith('video/') && !media.type?.startsWith('audio/') && (
              <div className="bg-white rounded-lg shadow-lg p-8 min-w-[500px] min-h-[400px] flex flex-col items-center justify-center">
                <FileText className="w-24 h-24 text-gray-400 mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{media.name}</h4>
                <p className="text-gray-500 mb-4">Document Preview</p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => onDownload(media)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Download className="w-4 h-4 inline mr-2" />
                    Download
                  </button>
                  <button
                    onClick={() => window.open(media.url, '_blank')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Open in New Tab
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Info - Simplified */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              <span>Press Esc to close</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onDownload(media)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                title="Download"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPreviewModal;
